/* =========================================================================
   Share data — export, import, reset, and the storage indicator.
   ========================================================================= */

import { openModal, closeModal, toast, registerActions } from './dom.js'
import { buildExport, importCollection, resetToSeed, recipes } from '../data/store.js'
import { storageEstimate, requestPersistence } from '../data/db.js'
import { formatBytes, recipeImageBytes } from '../data/images.js'
import { showList, setBanner } from '../app.js'

function fileName() {
  return 'killer-recipes-' + new Date().toISOString().slice(0, 10) + '.json'
}

export async function openIO() {
  const est = await storageEstimate()
  const persisted = await requestPersistence()
  const photoBytes = recipes.reduce((n, r) => n + recipeImageBytes(r), 0)
  const photoCount = recipes.reduce((n, r) => n + (r.heroImage ? 1 : 0) + (r.images?.length || 0), 0)

  const usageLine = est
    ? `Using <b>${formatBytes(est.usage)}</b>${est.quota ? ` of about ${formatBytes(est.quota)} available` : ''} · ${photoCount} photo${photoCount === 1 ? '' : 's'} (${formatBytes(photoBytes)})`
    : `${photoCount} photo${photoCount === 1 ? '' : 's'} stored`

  openModal(`
    <h3>Share the collection</h3>
    <p class="hint" style="margin-bottom:10px">
      Recipes are stored on this device. To share with family, export the collection and send
      them the file — they import it on their device.
    </p>

    <div class="actions" style="justify-content:flex-start">
      <button class="btn" data-act="exportJSON">⬇ Export collection</button>
      <button class="btn" data-act="importJSON">⬆ Import collection</button>
    </div>

    <label>Storage on this device</label>
    <div class="hint">
      ${usageLine}<br>
      ${persisted.persisted
        ? '✅ Storage is marked persistent — the browser won\'t clear it to reclaim space.'
        : persisted.supported
          ? '⚠️ Storage is not persistent yet. Add the app to your Home Screen and open it from there — Safari clears data for sites you haven\'t visited in about a week.'
          : '⚠️ This browser can\'t guarantee persistent storage. Export regularly.'}
    </div>

    <label>Start again</label>
    <div class="actions" style="justify-content:flex-start">
      <button class="btn danger" data-act="resetSeed">Reset to the 4 original recipes</button>
    </div>
    <div class="hint">This replaces everything currently stored, including photos. Export first.</div>

    <div class="actions"><button class="btn primary" data-act="closeModal">Done</button></div>`)
}

async function exportJSON() {
  toast('Preparing export…')
  try {
    const data = await buildExport()
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const name = fileName()

    // [iOS] a.download is unreliable in Safari and in an installed PWA.
    // The share sheet is the dependable route — it offers Files, Mail, AirDrop.
    const file = new File([blob], name, { type: 'application/json' })
    if (navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: 'Recipe collection' })
        toast('Exported')
        return
      } catch (e) {
        if (e?.name === 'AbortError') return       // user dismissed the sheet
        // otherwise fall through to the download link
      }
    }

    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = name
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(a.href), 10000)
    toast('Exported')
  } catch (e) {
    console.error(e)
    toast('Export failed — see the console for details')
  }
}

function importJSON() {
  const inp = document.createElement('input')
  inp.type = 'file'
  inp.accept = '.json,application/json'
  inp.onchange = () => {
    const f = inp.files[0]
    if (!f) return
    const rd = new FileReader()
    rd.onload = async () => {
      try {
        const data = JSON.parse(rd.result)
        const res = await importCollection(data)
        closeModal()
        showList()
        toast(`Imported ${res.total} — ${res.added} new, ${res.updated} updated`)
      } catch (e) {
        console.error(e)
        if (e?.name === 'QuotaExceededError') {
          setBanner('<b>Import ran out of space.</b> Some recipes may not have saved. Remove a few photos and try again.')
          closeModal()
        } else {
          toast("Couldn't read that file — is it a recipe export?")
        }
      }
    }
    rd.readAsText(f)
  }
  inp.click()
}

async function resetSeed() {
  if (!confirm('Replace everything currently stored with the 4 original recipes?\n\nPhotos and any recipes you have added will be removed. Export first if you want to keep them.')) return
  await resetToSeed()
  closeModal()
  showList()
  toast('Reset to the original 4 recipes')
}

registerActions({ openIO, exportJSON, importJSON, resetSeed })
